import styled from '@emotion/styled';
import { ConditionalPropResponse, Episode } from './apiService';
import { __t } from './i18n';
import Reaction from './Reaction';
import Rating from './Rating';
import { Card, Icon, Text, Box, Flex } from './styles';
import { durationToWords, getContentUrl, getTimerDuration, inspectFlags, usePlayerService } from './utils';
import { SpacingProps } from './withStyleSystem';
import useConditionalProps from './utils/useConditionalProps';
import CountdownTimer from './CountdownTimer';
import CardBadge from './CardBadge';
import { useRouter } from 'next/router';
import PlayButton from './PlayButton';
import { useEffect, useState } from 'react';

export default function EpisodeItem(props: EpisodeItemProps) {
  const { id, duration, rate_percentage, flags, conditional_flag, coming_soon, short_id } = props;
  const { selected } = inspectFlags(conditional_flag);

  return (
    <Container active={selected} direction="column" p={{ md: 1, lg: 2 }}>
      <EpisodeBody {...props} />
      <EpisodeFooter {...{ id, duration, rate_percentage, flags, coming_soon, short_id }} />
    </Container>
  );
}

// -------------- //
// SUB-COMPONENTS //
// -------------- //

const EpisodeBody = ({ short_id, slug, episode, rate_percentage,
  sub_title, conditional_flag, cover_image, flag, coming_soon }: EpisodeBodyProps) => {
  const url = getContentUrl({ short_id, slug });
  const thumbProps: EpisodeThumbProps = { cover_image, conditional_flag, flag, id: short_id, coming_soon, short_id, slug };
  const infoProps: EpisodeInfoProps = { url, episode, rate_percentage, sub_title, coming_soon };

  return (
    <Flex
      direction={{ md: 'column' }}
      mb={1}
    >
      <EpisodeThumb {...thumbProps} />
      <EpisodeInfo {...infoProps} py={1} />
    </Flex>
  );
};

const EpisodeThumb = ({ conditional_flag, cover_image, flag, id, coming_soon, short_id, slug }: EpisodeThumbProps) => {
  const flags = [conditional_flag, flag].join(', ');
  const { playable, free, comingSoon } = inspectFlags(flags);
  const router = useRouter();

  const [handleWatch, { loading }] = usePlayerService({ id, free });

  const url = `/contents/${short_id ?? id}/${slug}`;
  const canPlay = playable && !(coming_soon || comingSoon);
  const handleClick = () => {
    if (canPlay) {
      handleWatch('p');
    } else if (router.asPath !== url) {
      router.push(url);
    }
  };

  const image = cover_image?.path;

  return (
    <CardWrapper ml={{ xs: 2, md: 0 }} mb={{ md: 2, xs: 0.5 }} cursor={router.asPath !== url ? 'pointer' : 'auto'}>
      <Card onClick={handleClick} orientation="horizontal" style={{ backgroundImage: `url(${image}?w=330)` }}>
        {flags.length > 0 && <CardBadge flag={flag} customPosition="top" />}
        <ButtonWrapper style={{ opacity: loading ? 1 : undefined }}>
          {coming_soon || comingSoon || !playable ? null : <PlayButton loading={!!loading} />}
        </ButtonWrapper>
      </Card>
    </CardWrapper>
  );
};

const EpisodeInfo = ({ episode, rate_percentage, sub_title, url, coming_soon, ...spacing }: EpisodeInfoProps & SpacingProps) => {
  const router = useRouter();
  return <Box onClick={() => {
    if (router.asPath !== url) router.push(url);
  }} {...spacing} style={{ flexGrow: 1, cursor: router.asPath !== url ? 'pointer' : 'auto' }}>
    <Flex justify="space-between">
      {episode === 0 && sub_title ? sub_title : <EpisodeNumber number={episode} />}
      {!coming_soon && rate_percentage && <Rating rate={rate_percentage} mr={2} className="desktop-only" />}
    </Flex>
    <Text variant="body3" color="weak" weight="medium">
      {episode !== 0 && sub_title}
    </Text>
  </Box>;
};

const EpisodeNumber = ({ number }: { number?: number }) => (
  <Text variant="body1" weight="medium">
    {__t('content.type.episode')} <b>{number}</b>
  </Text>
);

const EpisodeFooter = ({ duration, id, rate_percentage, flags, coming_soon, short_id }: EpisodeFooterProps) => {
  const durationWords = duration && durationToWords(duration, true);
  const serverTime = useConditionalProps((s) => s.serverTime);
  const [timer, setTimer] = useState<number>(0);
  useEffect(() => {
    (serverTime && coming_soon) && setTimer(Number(getTimerDuration(coming_soon, serverTime)));
  }, [coming_soon, serverTime]);
  return (
    <Flex justify="space-between" align="baseline" direction={{ md: 'row-reverse' }} mt={{ xs: '0.25rem' }}>
      {timer
        ? <CountdownTimer
          timeDigitsWidth='1.7rem'
          value={Number(timer)}
          separatorPaddingY="0.25rem"
          containerCustomPadding="0.25rem"
          containerCustomRadius="0.25rem"
        />
        : <ReactionWrapper havePadding>
          <Reaction type="content" flag={flags || ''} id={short_id || id} inputSize="small" />
        </ReactionWrapper>
      }
      <Box>{durationWords && !coming_soon && <EpisodeDurationText color="weak" as="span" variant="body3" notShow={!!coming_soon}>
        {durationWords}
      </EpisodeDurationText>}
      {!timer && rate_percentage && <Rating rate={rate_percentage} mr={2} className="mobile-only" />}
      </Box>
    </Flex>
  );
};

// ------ //
// STYLES //
// ------ //

const Container = styled(Flex)<{ active: boolean }>(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    height: '100%',
    backgroundColor: theme.palette.background[700][0],
    borderRadius: theme.radius.medium,
    transition: 'background-color .3s ease',
    '&:hover': {
      backgroundColor: theme.palette.background[600][0],
      [theme.breakpoints.up('md')]: {
        [`${ButtonWrapper}`]: {
          transform: 'translateY(0)',
        },
      },
    },
  },
}));

const CardWrapper = styled(Box)<{ cursor?: 'pointer' | 'auto' }>(({ theme, cursor = 'pointer' }) => ({
  cursor,
  width: '50%',
  minWidth: '6.125rem',
  flexShrink: 0,
  [`${Card}`]: {
    borderRadius: theme.radius.default,
  },
  [theme.breakpoints.up('md')]: {
    width: '100%',
    [`${Card}`]: {
      boxShadow: '0 6px 9px rgba(0, 0, 0, 0.25)',
    },
  },
}));

const EpisodeDurationText = styled(Text)<{ notShow?: boolean }>(({ theme, notShow }) => ({
  [theme.breakpoints.between('md', 1120)]: {
    display: notShow ? 'none' : undefined,
  },
}));

const ReactionWrapper = styled.div<{ havePadding?: boolean }>(({ theme, havePadding }) => ({
  [theme.breakpoints.between('sm', 'md')]: {
    paddingTop: havePadding ? '0.32rem' : undefined,
  },
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  padding: theme.spacing(1),
  position: 'absolute',
  bottom: '50%',
  left: '50%',
  transform: 'translate(-50%, 50%)',
  [theme.breakpoints.up('md')]: {
    transition: 'transform .5s ease',
    transform: 'translateY(125%)',
    bottom: 0,
    left: 0,
  },
  [theme.breakpoints.down('md')]: {
    [`${Icon}`]: {
      fontSize: '.5rem',
    },
  },
}));

// ----- //
// TYPES //
// ----- //

type EpisodeItemProps = Episode & {
  flags?: ConditionalPropResponse['conditional_flag'];
};
type EpisodeBodyProps = Omit<EpisodeInfoProps & EpisodeThumbProps, 'url'> & Pick<Episode, 'short_id' | 'slug'>;
type EpisodeInfoProps = { url: string } & Pick<Episode, 'episode' | 'rate_percentage' | 'sub_title' | 'coming_soon'>;
type EpisodeThumbProps = Pick<Episode, 'conditional_flag' | 'cover_image' | 'flag' | 'id' | 'coming_soon' | 'slug' | 'short_id'>;
type EpisodeFooterProps = Pick<EpisodeItemProps, 'duration' | 'id' | 'rate_percentage' | 'flags' | 'coming_soon' | 'short_id'>;
